/**
 * PromoterDashboardWarningBar.tsx - Dashboard Warning and Alert Component
 * 
 * This component displays important warnings, alerts, and notifications
 * at the top of the promoter dashboard for critical information.
 * 
 * Key Features:
 * - Critical alerts and warning messages
 * - Fundraiser status notifications (expiring, paused, completed)
 * - Promoter-specific alerts and reminders
 * - Dismissible notifications with persistence
 * - Color-coded alert levels (info, warning, error)
 * - Action buttons for alert resolution
 * 
 * Alert Types:
 * - Fundraiser Expiring: Campaign ending soon
 * - Fundraiser Paused: Campaign temporarily stopped
 * - Goal Achieved: Fundraiser or promoter goal reached
 * - Minimum Not Met: Promoter below minimum requirements
 * - Reward Eligible: Promoter qualified for rewards
 * - Action Required: Promoter needs to take action
 * 
 * Visual Design:
 * - Color-coded backgrounds for alert severity
 * - Icons for quick alert type identification
 * - Dismissible with close button
 * - Responsive design for mobile devices
 * - Smooth animations for show/hide
 * 
 * Persistence:
 * - Remember dismissed alerts per promoter
 * - Re-show alerts when conditions change
 * - Respect user preferences for notifications
 * 
 * Props:
 * - promoterId: string - ID of promoter for personalized alerts
 * - fundraiserId: string - Associated fundraiser for context
 * 
 * Dependencies: MobX store, alert utilities, local storage
 */

import React from 'react';
import styled from 'styled-components';
import { observer } from 'mobx-react-lite';

const WarningBar = styled.div`
  background: #fef2f2;
  border: 1px solid #fca5a5;
  border-radius: 12px;
  padding: 16px 24px;
  margin: 24px 0;
  width: 100%;
  text-align: center;
  font-size: 18px;
  font-weight: 600;
  color: #dc2626;
  position: relative;
  z-index: 1;
`;

interface PromoterDashboardWarningBarProps {
  content: string;
}

export const PromoterDashboardWarningBar: React.FC<PromoterDashboardWarningBarProps> = observer(({ content }) => {
  return (
    <WarningBar>
      {content}
    </WarningBar>
  );
});